import { getPathAt } from '../../../utils/applyMenuConfigChanges/predicates';
import {
  createDefaultCategory,
  deleteEmptySortOrder,
} from '../../../utils/generateDefaultMenuConfig';
import {
  sortMembersBySortOrderFirst,
  bySortOrder,
} from '../../../utils/applyMenuConfigChanges/addSortOrderProperty/sortBySortOrder';
import { isBackOffice, isBranch } from '../../../utils/locationUtils';
import { isOneOfVeloViewers } from '../../../utils';
import { TYPES } from '../../../utils/flattenTree/utils/consts';
import { applyMenuConfigChanges } from '../../../utils/applyMenuConfigChanges';
import { deepCopy } from '../../../utils/objectUtils';

export const updateVisible = (path, config, type) => {
  const newConfig = [...config];
  const category = getCategoryByMemberPath(path, newConfig);
  const member = getMemberByPath(path, type, category);
  if (member) {
    member.visible = member.visible === true ? 'hidden' : true;
    category.touched = true;
    member.touched = true;
    return newConfig;
  }
};

const getCategoryByMemberPath = (path, config) => {
  const categoryPath = getPathAt(path, 0);
  return config.find((cat) => cat.path === categoryPath);
};

export const shouldAllowDrop = (draggedMember, dropOnMember) => {
  if (
    draggedMember.type !== TYPES.SECTION &&
    !hasSameSection(draggedMember, dropOnMember)
  ) {
    return false;
  }
  return draggedMember.type === dropOnMember.type;
};

export const hasSameCategory = (draggedItemPath, dropOnItemPath) => {
  return getPathAt(draggedItemPath, 0) === getPathAt(dropOnItemPath, 0);
};

export const shouldReplace = (draggedItem, dropOnItem) => {
  if (draggedItem && dropOnItem) {
    if (isCategory(draggedItem.path) !== isCategory(dropOnItem.path)) {
      alert('cannot replace category with member');
      return false;
    }
    if (getMemberLevel(draggedItem.path) !== getMemberLevel(dropOnItem.path)) {
      alert('cannot replace members with different levels');
      return false;
    }
    if (!hasSameSection(draggedItem, dropOnItem)) {
      alert('cannot replace members from different sections');
      return false;
    }
    if (draggedItem.type !== dropOnItem.type) {
      return false;
    }
  } else {
    // alert('one of the path cannot found');
    return false;
  }
  return true;
};

export const isCategory = (path) =>
  path && typeof path === 'string' && path.split('.').length === 1;

export const isFirstLevelSection = (path) =>
  path && typeof path === 'string' && path.split('.').length === 2;

export const getMemberLevel = (path) => path.split('.').length;

export const hasSameSection = (draggedItem, dropOnItem) => {
  if (draggedItem.type !== TYPES.SECTION && dropOnItem.type !== TYPES.SECTION) {
    const draggedItemSection = draggedItem.path.split('.').slice(0, -1).pop();
    const droppedItemSection = dropOnItem.path.split('.').slice(0, -1).pop();
    return draggedItemSection === droppedItemSection;
  }
  return true;
};

export const updateSortForMembers = (
  draggedItemMember,
  dropOnItemMember,
  config,
) => {
  const categoryOfDraggedMember = getPathAt(draggedItemMember.path, 0);
  const categoryOfDropOnMember = getPathAt(dropOnItemMember.path, 0);
  if (shouldReplace(draggedItemMember, dropOnItemMember)) {
    if (categoryOfDraggedMember !== categoryOfDropOnMember) {
      return sortMembersFromDifferentCategories(
        draggedItemMember,
        dropOnItemMember,
        config,
      );
    } else {
      return sortMembers(draggedItemMember, dropOnItemMember, config);
    }
  }
  return config;
};

const isBelongToSection = (member, draggedMember) =>
  (member.path === draggedMember.path && member.type === draggedMember.type) ||
  member.path.includes(`${draggedMember.path}.`);

const isNotBelongToSection = (member, draggedMember) =>
  (member.path !== draggedMember.path || member.type !== draggedMember.type) &&
  !member.path.includes(`${draggedMember.path}.`);

const addSectionToTargetCategoryAndSetInitialFlags = (
  targetCategory,
  draggedSectionAndMembers,
  draggedSection,
  sectionInitialSort,
  originalCategory,
) => {
  targetCategory.members.push(...draggedSectionAndMembers);
  setInitialFlagsForSection(
    draggedSection,
    originalCategory,
    sectionInitialSort,
  );
  targetCategory.members.sort(bySortOrder);
};

const removeSectionFromOriginalCategory = (_section, config) => {
  const originalCategory = getCategoryByMemberPath(_section.path, config);

  const sectionAndMembers = originalCategory.members.filter((member) =>
    isBelongToSection(member, _section),
  );

  const section = sectionAndMembers.find((s) => s.path === _section.path);

  originalCategory.members = originalCategory.members.filter((member) =>
    isNotBelongToSection(member, section),
  );

  return { originalCategory, sectionAndMembers, section };
};

const setNewSortIfNeeded = (targetCategory, draggedSection, newSortOrder) => {
  targetCategory.members.forEach((member) => {
    if (
      member.path !== draggedSection.path &&
      shouldUpdateSortOrder(draggedSection, member, true) &&
      isBiggerThanDraggedMember(member, draggedSection)
    ) {
      member.touched = true;
      member.sortOrder = newSortOrder;
      newSortOrder += 1;
    }
  });
};

const sortBySortOrderFirstAndMarkAsTouched = (category) => {
  category.members = sortMembersBySortOrderFirst(category.members);
  category.touched = true;
};

export const sortMembersFromDifferentCategories = (
  _draggedSection,
  targetItem,
  config,
) => {
  const newConfig = [...config];

  const {
    originalCategory,
    sectionAndMembers,
    section: draggedSection,
  } = removeSectionFromOriginalCategory(_draggedSection, newConfig);

  const targetCategory = getCategoryByMemberPath(targetItem.path, newConfig);

  sortMembersAndSetInitialSortOrder(targetCategory, draggedSection, true);

  const targetSection = getMemberByPath(
    targetItem.path,
    targetItem.type,
    targetCategory,
  );

  const initialSortOrder = targetSection.sortOrder + 1;

  addSectionToTargetCategoryAndSetInitialFlags(
    targetCategory,
    sectionAndMembers,
    draggedSection,
    initialSortOrder,
    originalCategory,
  );

  setNewSortIfNeeded(targetCategory, draggedSection, initialSortOrder + 1);
  sortBySortOrderFirstAndMarkAsTouched(targetCategory);

  return newConfig;
};

export const getMemberByPath = (path, type, category) =>
  category.members.find(
    (member) => member.path === path && member.type === type,
  );

export const sortMembers = (draggedItem, targetItem, config) => {
  const newConfig = [...config];
  const category = getCategoryByMemberPath(targetItem.path, newConfig);
  const draggedMember = getMemberByPath(
    draggedItem.path,
    draggedItem.type,
    category,
  );
  const targetMember = getMemberByPath(
    targetItem.path,
    targetItem.type,
    category,
  );

  sortMembersAndSetInitialSortOrder(category, draggedMember);
  draggedMember.sortOrder = targetMember.sortOrder + 1;
  const newSortOrder = targetMember.sortOrder + 2;

  category.members.sort(bySortOrder);
  setNewSortIfNeeded(category, draggedMember, newSortOrder);
  sortBySortOrderFirstAndMarkAsTouched(category);
  return newConfig;
};

const isBiggerThanDraggedMember = (member, draggedMember) =>
  member.sortOrder >= draggedMember.sortOrder;

export const createDefaultCategoryWithSortOrder = (newCategoryName, config) => {
  const newCategory = createDefaultCategory(newCategoryName);
  let newConfig = [...config];
  newCategory.touched = true;
  newConfig = newConfig.sort(bySortOrder);
  newConfig.unshift(newCategory);
  newConfig.forEach((category, i) => {
    category.sortOrder = i + 1;
    category.touched = true;
  });

  return newConfig;
};

const setInitialFlagsForSection = (
  section,
  originalCategory,
  initialSortOrder,
) => {
  section.sortOrder = initialSortOrder;
  section.touched = true;
  section.movedFrom = section.movedFrom || originalCategory.path;
};

const setNewSortForMembers = (
  category,
  initialSortOrder,
  draggedSection,
  fromDifferentCategory,
) => {
  category.members.forEach((member) => {
    if (shouldUpdateSortOrder(draggedSection, member, fromDifferentCategory)) {
      member.touched = true;
      member.sortOrder = initialSortOrder;
      initialSortOrder += 1;
    }
  });
};

export const enterSectionToCategory = (section, targetCategoryPath, config) => {
  const newConfig = [...config];
  const originalCategory = getCategoryByMemberPath(section.path, newConfig);
  const sectionAndMembers = originalCategory.members.filter((member) =>
    isBelongToSection(member, section),
  );

  const _section = sectionAndMembers.find((s) => s.path === section.path);

  originalCategory.members = originalCategory.members.filter((member) =>
    isNotBelongToSection(member, _section),
  );

  const targetCategory = newConfig.find(
    (cat) => cat.path === targetCategoryPath,
  );

  const initialSortOrder = 1;
  setInitialFlagsForSection(_section, originalCategory, initialSortOrder);

  if (targetCategory.members.length > 0) {
    setNewSortForMembers(targetCategory, initialSortOrder + 1, _section, true);
  }

  targetCategory.members.push(...sectionAndMembers);
  sortBySortOrderFirstAndMarkAsTouched(targetCategory);
  return newConfig;
};

export const updateCategoryName = (path, name, config) => {
  const newConfig = [...config];
  const category = getCategoryByMemberPath(path, newConfig);
  category.name = name;
  category.touched = true;
  return newConfig;
};

export const updateMemberName = (path, name, config, type) => {
  const newConfig = [...config];
  const category = getCategoryByMemberPath(path, newConfig);
  const member = getMemberByPath(path, type, category);
  member.name = name;
  category.touched = true;
  member.touched = true;
  return newConfig;
};

export const updateVisibleOfCategory = (path, config) => {
  const newConfig = [...config];
  const category = getCategoryByMemberPath(path, newConfig);
  category.visible = category.visible === true ? 'hidden' : true;
  category.touched = true;
  return newConfig;
};

export const updateSortForCategories = (
  draggedItemPath,
  dropOnItemPath,
  config,
) => {
  let newConfig = [...config];
  const draggedCategory = newConfig.find((cat) => cat.path === draggedItemPath);
  const dropOnCategory = newConfig.find((cat) => cat.path === dropOnItemPath);
  let newSortOrder = dropOnCategory.sortOrder + 2;
  draggedCategory.sortOrder = dropOnCategory.sortOrder + 1;
  newConfig = newConfig.sort(bySortOrder);

  newConfig.forEach((category) => {
    if (isSortOrderBiggerThenDraggedCategory(category, draggedCategory)) {
      category.sortOrder = newSortOrder;
      newSortOrder += 1;
    }
  });

  newConfig.forEach((category) => {
    category.touched = true;
  });
  return newConfig;
};

const isSortOrderBiggerThenDraggedCategory = (category, draggedCategory) =>
  category.sortOrder >= draggedCategory.sortOrder &&
  category.path !== draggedCategory.path;

export const getNewDocsTreeAfterApplyingMenuConfigChanges = (
  docsTree,
  menuConfig,
) => applyMenuConfigChanges(docsTree, { categories: menuConfig }, true);

export const exportConfig = (menuConfig, config) => {
  const touchedCategories = getOnlyTouchedCategories(
    menuConfig.categories,
    config,
  );
  if (touchedCategories.length > 0) {
    console.log('exported data: ', touchedCategories);
    exportToJsonFile(touchedCategories);
    window.localStorage.removeItem('menuEditorConfig');
  } else {
    alert('There is no changes');
  }
};

export const toIdFormat = (path) => path.split('.').join('-');

export const updateTreeForPreview = (
  store,
  menuEditorConfig,
  unchangedDocsTree,
) => {
  const config = deepCopy(menuEditorConfig);
  const docsTree = deepCopy(unchangedDocsTree);
  const touchedCategories = getOnlyTouchedCategories(
    store.viewer.menuConfig.categories,
    config,
  );
  if (touchedCategories.length > 0) {
    store._initDocsTree(docsTree, store.viewer, {
      categories: touchedCategories,
    });
  } else {
    alert('There is no changes');
  }
};

export const getOnlyTouchedCategories = (menuCategories, config) => {
  const menuCategoriesClone = deepCopy(menuCategories);
  const newConfig = deepCopy(config);

  menuCategoriesClone.forEach((menuConfigCategory) => {
    setHiddenCategoryForExport(menuConfigCategory, newConfig);
    const newConfigCategory = newConfig.find(
      (category) => category.path === menuConfigCategory.path,
    );
    if (newConfigCategory) {
      menuConfigCategory.members.forEach((menuConfigCategoryMember) => {
        setHiddenMemberForExport(menuConfigCategoryMember, newConfigCategory);
      });
    }
  });

  const touchedCategoriesAndMembers = newConfig
    // .filter((category) => category.touched)
    .map((category) => {
      deleteEmptySortOrder(category);
      category.members = category.members
        .filter((member) => member.touched)
        .map((member) => {
          delete member.touched;
          deleteEmptySortOrder(member);
          setOriginalCategoryInPathIfNeeded(member);
          return member;
        });
      return category;
    });
  touchedCategoriesAndMembers.forEach((cat) => {
    if (cat.visible === 'hidden') {
      cat.visible = false;
    }
    cat.members.forEach((member) => {
      if (member.visible === 'hidden') {
        member.visible = false;
      }
    });
  });

  return touchedCategoriesAndMembers;
};

export const setOriginalCategoryInPathIfNeeded = (member) => {
  if (member.movedFrom) {
    member.path = member.path.replace(
      member.path.split('.')[0],
      member.movedFrom,
    );
  }
};

const exportToJsonFile = (jsonData) => {
  const dataStr = JSON.stringify(jsonData);
  const dataUri =
    'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
  const exportFileDefaultName = 'menu-config.json';
  const linkElement = document.createElement('a');
  linkElement.setAttribute('href', dataUri);
  linkElement.setAttribute('download', exportFileDefaultName);
  linkElement.click();
};

const getMemberPathPrefix = (path) => {
  const pathKeys = path.split('.');
  pathKeys.pop();
  return pathKeys.join('.');
};

const shouldUpdateSortOrder = (
  draggedMember,
  currentMember,
  fromDifferentCategory,
) => {
  const equalLevel =
    getMemberLevel(currentMember.path) === getMemberLevel(draggedMember.path);
  const equalPathPrefix =
    fromDifferentCategory ||
    getMemberPathPrefix(currentMember.path) ===
      getMemberPathPrefix(draggedMember.path);

  const equalType = draggedMember.type === currentMember.type;

  return equalLevel && equalPathPrefix && equalType;
};

export const sortMembersAndSetInitialSortOrder = (
  category,
  draggedMember,
  fromDifferentCategory,
) => {
  let sortOrder = 1;
  category.members.forEach((member) => {
    if (shouldUpdateSortOrder(draggedMember, member, fromDifferentCategory)) {
      member.touched = true;
      member.sortOrder = sortOrder;
      sortOrder += 1;
    }
  });
  category.touched = true;
};

export const sortCategoriesAndSetInitialSortOrder = (categories) => {
  const sortedCategories = sortMembersBySortOrderFirst(categories);
  sortedCategories.forEach((category, i) => {
    category.sortOrder = i + 1;
    category.members = sortMembersBySortOrderFirst(category.members);
  });
  return sortedCategories;
};

export const sortBySortOrder = (initialConfig) => {
  sortCategoriesAndSetInitialSortOrder(initialConfig.categories);
  initialConfig.categories = initialConfig.categories.sort(bySortOrder);
};

export const initConfigFlagsForFutureExport = (menuConfig, initialConfig) => {
  menuConfig.forEach((menuConfigCategory) => {
    const initialConfigCategory = initialConfig.categories.find(
      (category) => category.path === menuConfigCategory.path,
    );
    if (initialConfigCategory) {
      setCategoryFlagsForFutureExport(
        initialConfigCategory,
        menuConfigCategory,
      );
      menuConfigCategory.members.forEach((menuConfigCategoryMember) => {
        setMemberFlagsForFutureExport(
          initialConfigCategory,
          menuConfigCategoryMember,
        );
      });
    }
  });
};

const setMemberFlagsForFutureExport = (
  initialConfigCategory,
  docsConfigCategoryMember,
) => {
  const memberToChange = initialConfigCategory.members.find(
    (member) =>
      member.path === docsConfigCategoryMember.path &&
      member.type === docsConfigCategoryMember.type,
  );

  if (memberToChange) {
    if (docsConfigCategoryMember.visible === false) {
      memberToChange.visible = 'hidden';
    }
    memberToChange.touched = true;
    memberToChange.sortOrder = docsConfigCategoryMember.sortOrder;
    if (
      memberToChange.type === 'SECTION' &&
      isFirstLevelSection(memberToChange.path)
    ) {
      memberToChange.movedFrom = docsConfigCategoryMember.movedFrom;
    }
  }
};

const setCategoryFlagsForFutureExport = (
  initialConfigCategory,
  docsConfigCategory,
) => {
  if (docsConfigCategory.visible === false) {
    initialConfigCategory.visible = 'hidden';
  }
  initialConfigCategory.touched = true;
  initialConfigCategory.sortOrder = docsConfigCategory.sortOrder;
};

const setHiddenCategoryForExport = (category, config) => {
  const categoryExist = config.some((c) => c.path === category.path);
  if (categoryExist) {
    return;
  }
  if (category.visible === false) {
    delete category.sortOrder;
    config.push(category);
  }
};

const setHiddenMemberForExport = (member, configCategory) => {
  const memberExist = configCategory.members.some(
    (_member) => _member.path === member.path,
  );
  if (memberExist) {
    return;
  }
  if (member.visible === false) {
    delete member.sortOrder;
    configCategory.members.push(member);
  }
};

export const withDoubleHeader = (location, viewer) =>
  isBranch(location) &&
  !viewer.isInternalViewer() &&
  !isOneOfVeloViewers(viewer.category);

export const withDCHeader = (location, viewer) =>
  !viewer.isInternalViewer() &&
  !isBranch(location) &&
  !isBackOffice(location) &&
  !isOneOfVeloViewers(viewer.category);

export const withBranchHeader = (location, viewer) =>
  isBranch(location) && viewer.isInternalViewer();

export const shouldAllowDropOnCategory = (type) => {
  const allowToTypes = {
    [TYPES.SECTION]: true,
    [TYPES.CATEGORY]: true,
  };
  return allowToTypes[type];
};
